import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectDynamicFilter,
  resetDynamicFilters
} from '@store/redux/actions/filterActions'
import { getCustomerFitCondensed } from '@store/redux/selectors/customerFitSelectors'
import { analytics } from '@helpers/analytics'
import { getFiltersOptionsState } from '@helpers/filters'
import styles from './styles.module.scss'
import Note from '@components/ui/Note'
import HelpButton from '@components/ui/HelpButton'
import { Fit } from 'types/fit'
import { OPEN_FIT_FINDER } from '@consts/index'

type SavedFitBoxProps = {
  optionTypesToShow: string[]
  appliedFilterOptions: {
    type: string
    value: string
  }[]
  variant?: 'dot' | 'border'
  showApplyButton: boolean
}

function SavedFitBox({
  optionTypesToShow = [],
  appliedFilterOptions = [],
  variant = 'border',
  showApplyButton = true
}: SavedFitBoxProps) {
  const [isNoteVisible, setIsNoteVisible] = useState(false)
  const { fit }: { fit: Partial<Fit> } = useSelector(getCustomerFitCondensed)
  const dispatch = useDispatch()
  const optionTypesToRender = optionTypesToShow.filter(
    type => fit[type as keyof Fit]
  )

  const isSavedFitApplied =
    !!appliedFilterOptions.length &&
    appliedFilterOptions
      .filter(option => optionTypesToRender.includes(option.type))
      .every(
        option => String(option.value) === String(fit[option.type as keyof Fit])
      )

  const hasAtLeastTwoOptionsToRender = optionTypesToRender.length > 1

  const handleOnApply = () => {
    const savedFitFiltersState = getFiltersOptionsState(fit)
    dispatch(resetDynamicFilters())
    dispatch(selectDynamicFilter(savedFitFiltersState))
    analytics('Click', {
      category: 'Saved Fit Box',
      label: 'Apply'
    })
  }

  const handleNoteToggle = () => {
    const noteNewState = !isNoteVisible
    setIsNoteVisible(noteNewState)
    analytics('Click', {
      category: 'Saved Fit Box',
      label: noteNewState
        ? 'Saved Fit tooltip - open'
        : 'Saved Fit tooltip - close'
    })
  }

  useEffect(() => {
    if (hasAtLeastTwoOptionsToRender) {
      analytics('Saved Fit Displayed')
    }
  }, [hasAtLeastTwoOptionsToRender])

  if (!hasAtLeastTwoOptionsToRender) {
    return null
  }

  return (
    <>
      <section
        className={`${styles.SavedFitBox} ${styles[`variant_${variant}`]} ${
          isSavedFitApplied ? styles.isApplied : ''
        }`}
      >
        <p className={styles.SavedFitBox__title}>
          Your Fit{' '}
          <HelpButton
            active={isNoteVisible}
            onClick={handleNoteToggle}
            classes={styles.SavedFitBox__helperButton}
          />
        </p>
        <div className={styles.SavedFitBox__optionsContainer}>
          <div className={styles.SavedFitBox__options}>
            {optionTypesToRender.map(type => (
              <div className={styles.SavedFitBox__option} key={type}>
                <span className={styles.SavedFitBox__optionType}>{type}</span>
                <span className={styles.SavedFitBox__optionValue}>
                  {fit[type as keyof Fit]}
                </span>
              </div>
            ))}
          </div>
          {showApplyButton &&
            (isSavedFitApplied ? (
              <span className={styles.SavedFitBox__applied}>Is applied</span>
            ) : (
              <button onClick={handleOnApply}>Apply</button>
            ))}
        </div>
      </section>
      {isNoteVisible && (
        <Note classes={styles.SavedFitBox__note}>
          You will see your recommended Fit throughout your shopping experience.
          If you need to tweak your size{' '}
          <a href={`#${OPEN_FIT_FINDER}`} title="Go to Fit finder">
            click here
          </a>
          .
        </Note>
      )}
    </>
  )
}

export default SavedFitBox
